'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

/**
 * True when the current user is a member of the Ballee org — a human support
 * responder (masked as "Ballee"). Backs the client-side support-responder gates
 * that used to key off super-admin only, so a non-super-admin Ballee member
 * (e.g. Philippine) can respond to support from the normal Inbox.
 *
 * Uses the `is_ballee_support_responder()` RPC (resolves the Ballee org by slug
 * server-side, env-safe). Cached like `useIsSuperAdmin`.
 */
export function useIsBalleeSupportResponder() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['is-ballee-support-responder'],
    queryFn: async () => {
      // RPC lands in the same change; generated types regenerate in CI, so
      // call through a narrow untyped view until then.
      const untyped = client as unknown as {
        rpc: (fn: string) => Promise<{ data: unknown; error: unknown }>;
      };
      const { data, error } = await untyped.rpc('is_ballee_support_responder');

      if (error) {
        console.error('Error checking Ballee support responder status:', error);
        return false;
      }

      return data === true;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
